import { useCallback, useEffect, useRef, useState } from "react";
import { useWorkToastStore, type WorkToastItem } from "@/stores/work-toast-store";

export const ANSWERED_LINGER_MS = 1_500;

/** Spec §7: a question answered elsewhere while its card is open shows
 *  "Answered" for 1.5 s. The item may be gone by then (untracked questions are
 *  removed), so the stack keeps the last snapshot instead of reading the store.
 *
 *  `active` must be the same "this window is the one actually showing the
 *  stack" flag the caller uses to gate rendering. This hook mounts in every
 *  window (the store is mirrored), so without that flag every window's timer
 *  would race to call `collapse()` on the shared store — only the rendering
 *  window may write back to it. */
export function useAnsweredQuestion(
  items: Record<string, WorkToastItem>,
  expandedKey: string | null,
  active: boolean,
): { answered: WorkToastItem | null; clear: () => void } {
  const watchedRef = useRef<WorkToastItem | null>(null);
  const [answered, setAnswered] = useState<WorkToastItem | null>(null);

  useEffect(() => {
    const watched = watchedRef.current;
    const expanded = expandedKey ? items[expandedKey] : undefined;
    if (expanded?.state === "question" && expanded.question) {
      watchedRef.current = expanded;
      return;
    }
    watchedRef.current = null;
    if (!watched?.question) return;
    const current = items[watched.key];
    // Still pending: the user collapsed the card, nothing was answered.
    if (current?.question?.awaitingInputId === watched.question.awaitingInputId) return;
    setAnswered(watched);
  }, [expandedKey, items]);

  useEffect(() => {
    if (!answered) return;
    const timer = window.setTimeout(() => {
      setAnswered(null);
      if (!active) return; // a non-rendering window never reaches into the store
      const store = useWorkToastStore.getState();
      if (store.expandedKey === answered.key) store.collapse();
    }, ANSWERED_LINGER_MS);
    return () => window.clearTimeout(timer);
  }, [answered, active]);

  const clear = useCallback(() => setAnswered(null), []);
  return { answered, clear };
}
